import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { getCategoryMeta } from '@/mocks/categories';
import { formatDateShort } from '@/utils/articles';

interface SlideItem {
  article: Article;
  image: string;
}

interface HeroCarouselProps {
  items: SlideItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (next: number) => setIndex((next + items.length) % items.length);
  const current = items[index];

  return (
    <section className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-foreground-950">
      {items.map((item, i) => (
        <div
          key={item.article.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.image}
            alt={item.article.title}
            title={`${item.article.title} - 91吃瓜`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/60" />
        </div>
      ))}

      <div
        className="relative z-10 h-full w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-between pt-24 pb-28 md:pb-36"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          const delta = e.changedTouches[0].clientX - touchStartX.current;
          if (delta > 50) go(index - 1);
          if (delta < -50) go(index + 1);
        }}
      >
        <div className="animate-fade-in">
          <span className="inline-flex items-center gap-2 text-primary-300 text-xs md:text-sm font-medium tracking-wide">
            <span className="w-6 h-[2px] bg-primary-400" />
            今日焦点 · 娱乐风向标
          </span>
          <h1 className="mt-3 font-heading font-black text-xl md:text-3xl text-background-50 max-w-3xl leading-snug">
            91吃瓜 - 最新娱乐新闻与网络热点资讯平台
          </h1>
        </div>

        <div key={current.article.id} className="animate-fade-up max-w-3xl">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 rounded text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
              {getCategoryMeta(current.article.category)?.name}
            </span>
            <span className="text-xs text-background-200/80 whitespace-nowrap">
              {formatDateShort(current.article.publishTime)}
            </span>
          </div>
          <h2 className="mt-3 font-heading font-black text-2xl md:text-4xl text-background-50 leading-tight line-clamp-2">
            <Link to={`/detail/${current.article.id}`} className="hover:text-primary-200 transition-colors cursor-pointer">
              {current.article.title}
            </Link>
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-200/85 hidden sm:block line-clamp-2 max-w-2xl">
            {current.article.summary}
          </p>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${current.article.id}`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <div className="flex items-center gap-2">
              {items.map((item, i) => (
                <button
                  key={item.article.id}
                  type="button"
                  aria-label={`切换到第 ${i + 1} 张`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all cursor-pointer ${
                    i === index ? 'w-6 bg-primary-400' : 'w-3 bg-background-50/40'
                  }`}
                />
              ))}
            </div>
            <div className="hidden md:flex items-center gap-2 ml-2">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => go(index - 1)}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => go(index + 1)}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}